import React from 'react';
import '@/App.css';
import AliasClaimModal from '@/components/AliasClaimModal';
import EulaGate from '@/components/EulaGate';
import { AiHelpLauncher } from '@/components/AiHelpDialog';

const AdminFinancialsPageLazy = React.lazy(() => import('@/pages/admin/AdminFinancialsPage'));
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth_context';
import { ProWriterProvider } from '@/lib/store';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AppShell } from '@/components/AppShell';
import { MobileWorkbenchShell } from '@/components/workbench/MobileWorkbenchShell';
import { PaywallProvider } from '@/components/dialogs/PaywallProvider';
import DunningBanner from '@/components/DunningBanner';
import GrandfatherBanner from '@/components/GrandfatherBanner'; // Feb-2026 · 90-day grace period notice
import ResignBanner from '@/components/ResignBanner';        // Feb-2026 · contract v1.1 nudge
import GuidedTour from '@/components/GuidedTour';
import GlowNudge from '@/components/GlowNudge';
import UpgradeManager from '@/components/UpgradeManager';
import SessionTimeoutWarning from '@/components/SessionTimeoutWarning';
import TrialExpiredScreen from '@/components/TrialExpiredScreen';
import PreLaunchBanner from '@/components/PreLaunchBanner';
import PaymentDowntimeBanner from '@/components/PaymentDowntimeBanner';   // iter-payments-master
import AdminPage from '@/pages/AdminPage';
import AdminExecDock from '@/components/AdminExecDock';       // Feb-2026 · floating exec + ledger dock
import AdminHubPage from '@/pages/AdminHubPage';
import AdminResetPassword from '@/pages/AdminResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import LoginPage from '@/pages/LoginPage';
import LandingView from '@/components/landing/LandingView';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useSubscriptionReturn } from '@/lib/useSubscriptionReturn';
import WritersSalonModal from '@/components/landing/scriptorium/WritersSalonModal';
import TrainingPage from '@/pages/TrainingPage';
import ManuscriptLibraryPage from '@/pages/ManuscriptLibraryPage';
import HowWeHelpWritersPage from '@/pages/HowWeHelpWritersPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AppealPage from '@/pages/AppealPage';
import TrustPage from '@/pages/TrustPage';
import DiversitySustainabilityPage from '@/pages/DiversitySustainabilityPage';
import EmailVerificationPage from '@/pages/EmailVerificationPage';
import LoomPage from '@/pages/LoomPage';
import LoomEditorInboxPage from '@/pages/LoomEditorInboxPage';
import BookstoreLanding from '@/pages/BookstoreLanding';
import SwagSignupPage from '@/pages/SwagSignupPage';        // Feb-2026 · grand-opening capture
import SyncReportPage from '@/pages/SyncReportPage';        // Feb-2026 · author sync report
import GenrePage from '@/pages/bookstore/GenrePage';
import BookstoreAuthorPage from '@/pages/bookstore/AuthorPage';   // Feb-2026 · bookstore pen-name bio/book listing page
import WorldPage from '@/pages/bookstore/WorldPage';               // Feb-2026 · shared-universe collection landing page
import AdultsOnlyLanding from '@/pages/bookstore/AdultsOnlyLanding';   // Feb-2026 · 18+ super-shelf
import CommunityDiverseLanding from '@/pages/bookstore/CommunityDiverseLanding';
import AIBooksPage from '@/pages/bookstore/AIBooksPage';   // iter-p3-r
import BookstoreLogin from '@/pages/bookstore/BookstoreLogin';
import BookstoreResetPassword from '@/pages/bookstore/BookstoreResetPassword';
import BookstoreAccount from '@/pages/bookstore/BookstoreAccount';
import BookstoreAdmin from '@/pages/bookstore/BookstoreAdmin';
import FAQPage from '@/pages/FAQPage';   // iter-p3-r
import WhatWeOfferPage from '@/pages/WhatWeOfferPage';   // 2026-10
import BlogIndexPage from '@/pages/BlogIndexPage';   // 2026-10
import BlogPostPage from '@/pages/BlogPostPage';   // 2026-10
import ComparisonPage     from '@/pages/ComparisonPage';   // Feb-2026 — AEO comparison pages
import CompareIndexPage  from '@/pages/CompareIndexPage';  // Oct-2026 — /compare index
import PricingPage from '@/pages/PricingPage';           // Feb-2026 — public pricing page
import FamilyAcceptPage from '@/pages/FamilyAcceptPage'; // family invite accept flow
import CommunityPage from '@/pages/CommunityPage';
import CodexBrowsePage from '@/pages/CodexBrowsePage';
import MobileNotebookPage from '@/pages/MobileNotebookPage';
import NovelFrameworkPage from '@/pages/NovelFrameworkPage';
import PanelWindowPage from '@/pages/PanelWindowPage';
import StacksPage from '@/pages/StacksPage';
import FundsPage from '@/pages/FundsPage';
// iter-53n++ pass 6 — Community templates marketplace.
import MarketplacePage from '@/pages/MarketplacePage';
import BusinessDashboard from '@/pages/BusinessDashboard';
import AdminMarketingBookings from '@/pages/admin/AdminMarketingBookings';
import AdminEmailTranslator from '@/pages/admin/AdminEmailTranslator';   // iter-p2-z
const AdminEmailLogPage = React.lazy(() => import('@/pages/admin/AdminEmailLogPage'));
import AdminInboxPanel from '@/pages/admin/AdminInboxPanel';   // iter-p3-b
import ReaderLanguagesInsightPanel from '@/pages/admin/ReaderLanguagesInsightPanel';   // iter-p3-h
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import HelpUnsubscribePage from '@/pages/HelpUnsubscribePage';
import EditorApp from '@/pages/editor/EditorApp';  // Feb-2026 editor shell
import ClarityTracker from '@/components/ClarityTracker';
import { bootstrapTheme } from '@/lib/theme';   // iter-palette-commit
import { useIsSmallPhone } from '@/hooks/useIsMobile';
import { SmallDeviceBookstoreNotice } from '@/components/bookstore/SmallDeviceBookstoreNotice';
import { BookstoreAuthProvider } from '@/lib/bookstoreAuthContext';
import { DemoProvider } from '@/lib/demo_context';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import AuthorPage from '@/pages/AuthorPage';
import MyLibraryPage from '@/pages/MyLibraryPage';   // iter-p2-m
import ReadFreeSamplePage from '@/pages/ReadFreeSamplePage';   // iter-p2-n
import BundleLandingPage from '@/pages/bookstore/BundleLandingPage';   // iter-p2-o
import BookLandingPage from '@/pages/BookLandingPage';   // 2026-02 · QR-scan destination for print back-covers
import QADashboardPage from '@/pages/QADashboardPage';   // iter-qa-dashboard
import EditingReviewWorkspacePage from '@/pages/EditingReviewWorkspacePage';   // Feb-2026 QA/Editor collab
import QANavPill       from '@/components/chrome/QANavPill';  // iter-qa-nav-pill
import ClarityLoader   from '@/components/chrome/ClarityLoader';  // Feb-2026 heatmap
import GenrePreviewDemo from '@/pages/GenrePreviewDemo';
import LocaleRoute from '@/components/LocaleRoute';  // 2026-02 browser-language + hreflang routing
import { Loader2, BookOpen } from 'lucide-react';

// ── Global hreflang provider for English-only public pages ─────────────────
// Automatically injects <link rel="alternate" hreflang="x-default"> and
// <link rel="alternate" hreflang="en"> for every public page that does NOT
// have its own full multi-locale hreflang (those pages call useHreflang
// directly). This fills the SEO/GEO gap so search bots always see a language
// signal even on content-only pages (FAQ, Blog, Trust, etc.).
const SKIP_HREFLANG_PREFIXES = [
  '/app', '/admin', '/editor', '/loom', '/qa', '/notebook',
  '/framework', '/panel-window', '/training', '/manuscript-library',
  '/community', '/stacks', '/funds', '/my-library', '/payment', '/help',
  '/verify', '/login',
];
const FULL_HREFLANG_EXACT = new Set(['/', '/writers', '/bookstore', '/how-we-help-writers', '/blog']);
const LOCALE_ROUTE_PREFIXES = ['/fr', '/de', '/es', '/pt', '/zh'];

function GlobalPublicHreflang() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    // Skip: landing root or locale-prefix routes (ScriptoriumLanding / LocaleRoute handles them)
    if (FULL_HREFLANG_EXACT.has(pathname)) return;
    if (LOCALE_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;
    // Skip: authenticated workspaces and utility routes
    if (SKIP_HREFLANG_PREFIXES.some((r) => pathname === r || pathname.startsWith(`${r}/`))) return;

    const href = window.location.origin + pathname;
    const xdef = Object.assign(document.createElement('link'), {
      rel: 'alternate', hreflang: 'x-default', href,
    });
    const enEl = Object.assign(document.createElement('link'), {
      rel: 'alternate', hreflang: 'en', href,
    });
    document.head.appendChild(xdef);
    document.head.appendChild(enEl);

    // Task 4: Reset <html lang="en"> for English-only pages so bots and
    // assistive technology read the correct content language. LocaleRoute
    // overwrites this when the user navigates back to a locale route.
    const prevLang = document.documentElement.lang;
    if (prevLang !== 'en') document.documentElement.lang = 'en';

    return () => {
      xdef.parentNode?.removeChild(xdef);
      enEl.parentNode?.removeChild(enEl);
      if (prevLang !== 'en') document.documentElement.lang = prevLang;
    };
  }, [pathname]);

  return null;
}

function AppRouter() {
  const location = useLocation();
  // iter-palette-commit — Fetch + apply admin-committed theme overrides
  // once per SPA session. Runs before any Route mounts so the entire
  // app renders with the committed palette.
  React.useEffect(() => { bootstrapTheme(); }, []);
  // Synchronous detection of OAuth callback fragment to avoid race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ClarityLoader />
      <QANavPill />
      <GlobalPublicHreflang />
      <Routes>
      {/* Public marketing surfaces (iter-23) */}
      {/* iter-50p — device-aware: desktop sees QuillAndVineLanding; mobile sees the writers landing with a redirect-to-bookstore CTA. */}
      <Route path="/"               element={<DeviceLandingRouter />} />
      <Route path="/writers"        element={<DeviceLandingRouter />} />
      {/* Locale-prefixed landing pages for crawlers + browser-language auto-routing */}
      <Route path="/fr" element={<LocaleRoute lang="fr"><DeviceLandingRouter /></LocaleRoute>} />
      <Route path="/de" element={<LocaleRoute lang="de"><DeviceLandingRouter /></LocaleRoute>} />
      <Route path="/es" element={<LocaleRoute lang="es"><DeviceLandingRouter /></LocaleRoute>} />
      <Route path="/pt" element={<LocaleRoute lang="pt"><DeviceLandingRouter /></LocaleRoute>} />
      <Route path="/zh" element={<LocaleRoute lang="zh"><DeviceLandingRouter /></LocaleRoute>} />
      {/* Locale-prefixed bookstore + marketing pages — full 6-locale hreflang via the component */}
      <Route path="/fr/bookstore"            element={<LocaleRoute lang="fr"><DeviceBookstoreRouter /></LocaleRoute>} />
      <Route path="/de/bookstore"            element={<LocaleRoute lang="de"><DeviceBookstoreRouter /></LocaleRoute>} />
      <Route path="/es/bookstore"            element={<LocaleRoute lang="es"><DeviceBookstoreRouter /></LocaleRoute>} />
      <Route path="/pt/bookstore"            element={<LocaleRoute lang="pt"><DeviceBookstoreRouter /></LocaleRoute>} />
      <Route path="/zh/bookstore"            element={<LocaleRoute lang="zh"><DeviceBookstoreRouter /></LocaleRoute>} />
      <Route path="/fr/how-we-help-writers"  element={<LocaleRoute lang="fr"><HowWeHelpWritersPage /></LocaleRoute>} />
      <Route path="/de/how-we-help-writers"  element={<LocaleRoute lang="de"><HowWeHelpWritersPage /></LocaleRoute>} />
      <Route path="/es/how-we-help-writers"  element={<LocaleRoute lang="es"><HowWeHelpWritersPage /></LocaleRoute>} />
      <Route path="/pt/how-we-help-writers"  element={<LocaleRoute lang="pt"><HowWeHelpWritersPage /></LocaleRoute>} />
      <Route path="/zh/how-we-help-writers"  element={<LocaleRoute lang="zh"><HowWeHelpWritersPage /></LocaleRoute>} />
      {/* Locale-prefixed blog — full 7-locale hreflang via BlogIndexPage/BlogPostPage */}
      <Route path="/fr/blog"                 element={<LocaleRoute lang="fr"><BlogIndexPage /></LocaleRoute>} />
      <Route path="/de/blog"                 element={<LocaleRoute lang="de"><BlogIndexPage /></LocaleRoute>} />
      <Route path="/es/blog"                 element={<LocaleRoute lang="es"><BlogIndexPage /></LocaleRoute>} />
      <Route path="/pt/blog"                 element={<LocaleRoute lang="pt"><BlogIndexPage /></LocaleRoute>} />
      <Route path="/zh/blog"                 element={<LocaleRoute lang="zh"><BlogIndexPage /></LocaleRoute>} />
      <Route path="/fr/blog/:slug"           element={<LocaleRoute lang="fr"><BlogPostPage /></LocaleRoute>} />
      <Route path="/de/blog/:slug"           element={<LocaleRoute lang="de"><BlogPostPage /></LocaleRoute>} />
      <Route path="/es/blog/:slug"           element={<LocaleRoute lang="es"><BlogPostPage /></LocaleRoute>} />
      <Route path="/pt/blog/:slug"           element={<LocaleRoute lang="pt"><BlogPostPage /></LocaleRoute>} />
      <Route path="/zh/blog/:slug"           element={<LocaleRoute lang="zh"><BlogPostPage /></LocaleRoute>} />
      {/* Locale-prefixed Trust & Diversity pages — crawlers get hreflang per locale */}
      <Route path="/fr/trust"                element={<LocaleRoute lang="fr"><TrustPage /></LocaleRoute>} />
      <Route path="/de/trust"                element={<LocaleRoute lang="de"><TrustPage /></LocaleRoute>} />
      <Route path="/es/trust"                element={<LocaleRoute lang="es"><TrustPage /></LocaleRoute>} />
      <Route path="/pt/trust"                element={<LocaleRoute lang="pt"><TrustPage /></LocaleRoute>} />
      <Route path="/zh/trust"                element={<LocaleRoute lang="zh"><TrustPage /></LocaleRoute>} />
      <Route path="/fr/diversity-sustainability" element={<LocaleRoute lang="fr"><DiversitySustainabilityPage /></LocaleRoute>} />
      <Route path="/de/diversity-sustainability" element={<LocaleRoute lang="de"><DiversitySustainabilityPage /></LocaleRoute>} />
      <Route path="/es/diversity-sustainability" element={<LocaleRoute lang="es"><DiversitySustainabilityPage /></LocaleRoute>} />
      <Route path="/pt/diversity-sustainability" element={<LocaleRoute lang="pt"><DiversitySustainabilityPage /></LocaleRoute>} />
      <Route path="/zh/diversity-sustainability" element={<LocaleRoute lang="zh"><DiversitySustainabilityPage /></LocaleRoute>} />
      {/* Locale-prefixed FAQ — hreflang alternates for search bots */}
      <Route path="/fr/faq"                  element={<LocaleRoute lang="fr"><FAQPage /></LocaleRoute>} />
      <Route path="/de/faq"                  element={<LocaleRoute lang="de"><FAQPage /></LocaleRoute>} />
      <Route path="/es/faq"                  element={<LocaleRoute lang="es"><FAQPage /></LocaleRoute>} />
      <Route path="/pt/faq"                  element={<LocaleRoute lang="pt"><FAQPage /></LocaleRoute>} />
      <Route path="/zh/faq"                  element={<LocaleRoute lang="zh"><FAQPage /></LocaleRoute>} />
      {/* Locale-prefixed What We Offer */}
      <Route path="/fr/what-we-offer"        element={<LocaleRoute lang="fr"><WhatWeOfferPage /></LocaleRoute>} />
      <Route path="/de/what-we-offer"        element={<LocaleRoute lang="de"><WhatWeOfferPage /></LocaleRoute>} />
      <Route path="/es/what-we-offer"        element={<LocaleRoute lang="es"><WhatWeOfferPage /></LocaleRoute>} />
      <Route path="/pt/what-we-offer"        element={<LocaleRoute lang="pt"><WhatWeOfferPage /></LocaleRoute>} />
      <Route path="/zh/what-we-offer"        element={<LocaleRoute lang="zh"><WhatWeOfferPage /></LocaleRoute>} />
      {/* Locale-prefixed Marketplace */}
      <Route path="/fr/marketplace"          element={<LocaleRoute lang="fr"><MarketplacePage /></LocaleRoute>} />
      <Route path="/de/marketplace"          element={<LocaleRoute lang="de"><MarketplacePage /></LocaleRoute>} />
      <Route path="/es/marketplace"          element={<LocaleRoute lang="es"><MarketplacePage /></LocaleRoute>} />
      <Route path="/pt/marketplace"          element={<LocaleRoute lang="pt"><MarketplacePage /></LocaleRoute>} />
      <Route path="/zh/marketplace"          element={<LocaleRoute lang="zh"><MarketplacePage /></LocaleRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/appeal"         element={<AppealPage />} />
      <Route path="/trust"          element={<TrustPage />} />
      <Route path="/diversity-sustainability" element={<DiversitySustainabilityPage />} />
      <Route path="/diversity-and-sustainability" element={<Navigate to="/diversity-sustainability" replace />} />
      <Route path="/dei" element={<Navigate to="/diversity-sustainability" replace />} />
      <Route path="/verify-recovery-email" element={<EmailVerificationPage kind="recovery" />} />
      <Route path="/verify-login-email" element={<EmailVerificationPage kind="login" />} />
      <Route path="/loom"           element={<MobileWorkbenchShell surface="loom"><LoomPage /></MobileWorkbenchShell>} />
      <Route path="/loom/editor"    element={<MobileWorkbenchShell surface="loom"><LoomEditorInboxPage /></MobileWorkbenchShell>} />
      {/* iter-qa-dashboard — QA reviewer inbox. Role-gated inside the
       * page (require role=editor). The seeded QA account
       * quality@crownnrose.com lands here after sign-in. */}
      <Route path="/qa"             element={<QADashboardPage />} />
      <Route path="/qa/review/:rid" element={<EditingReviewWorkspacePage />} />
      <Route path="/editor/review/:rid" element={<EditingReviewWorkspacePage />} />
      <Route path="/quality"        element={<Navigate to="/qa" replace />} />
      {/* iter55p5 — public genre-blend demo (no auth) for marketing screenshots. */}
      <Route path="/demo/genre-preview" element={<GenrePreviewDemo />} />
      {/* iter55p14 — public author deep-link. */}
      <Route path="/authors/:penSlug" element={<AuthorPage />} />
      <Route path="/my-library"       element={<MyLibraryPage />} />
      <Route path="/read-free/:slug"  element={<ReadFreeSamplePage />} />
      <Route path="/bookstore/bundles/:slug" element={<BundleLandingPage />} />
      {/* 2026-02 · Public /book/:isbn — target of the QR code that
       *  sits beside the EAN-13 on every Crown & Rose print back cover. */}
      <Route path="/book/:isbn" element={<BookLandingPage />} />
      <Route path="/bookstore"      element={<DeviceBookstoreRouter />} />
      <Route path="/bookstore/swag" element={<SwagSignupPage />} />

      <Route path="/faq"            element={<FAQPage />} />
      <Route path="/what-we-offer"  element={<WhatWeOfferPage />} />
      <Route path="/blog"           element={<BlogIndexPage />} />
      <Route path="/blog/:slug"     element={<BlogPostPage />} />
      <Route path="/compare"        element={<CompareIndexPage />} />   {/* Oct-2026 — all comparisons index */}
      <Route path="/compare/:slug"  element={<ComparisonPage />} />    {/* Feb-2026 — AEO comparison pages */}
      <Route path="/pricing"        element={<PricingPage />} />       {/* Feb-2026 — public pricing page */}
      <Route path="/family/accept"  element={<FamilyAcceptPage />} />  {/* family invite accept */}
      <Route path="/how-we-help-writers" element={<HowWeHelpWritersPage />} />
      <Route path="/how-we-help"         element={<Navigate to="/how-we-help-writers" replace />} />
      {/* Bookstore surfaces (iter-24) */}
      <Route path="/bookstore/g/adults-only" element={<AdultsOnlyLanding />} />
      <Route path="/bookstore/g/our-community-is-diverse" element={<CommunityDiverseLanding />} />
      <Route path="/bookstore/g/:slug"   element={<GenrePage />} />
      <Route path="/bookstore/author/:slug" element={<BookstoreAuthorPage />} />
      <Route path="/bookstore/world/:slug" element={<WorldPage />} />
      <Route path="/bookstore/ai-books"  element={<AIBooksPage />} />
      <Route path="/bookstore/login"     element={<BookstoreLogin />} />
      <Route path="/bookstore/reset-password" element={<BookstoreResetPassword />} />
      <Route path="/bookstore/account"   element={<BookstoreAccount />} />
      <Route path="/bookstore/admin"     element={<BookstoreAdmin />} />
      {/* Auth + admin */}
      <Route path="/admin"            element={<MobileWorkbenchShell surface="admin"><AdminHubPage /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/admin"      element={<MobileWorkbenchShell surface="admin"><AdminPage group="admin" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/financials" element={<MobileWorkbenchShell surface="admin"><AdminFinancialsPageLazy /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route path="/admin/finance"    element={<MobileWorkbenchShell surface="admin"><AdminPage group="finance" /></MobileWorkbenchShell>} />
      <Route path="/admin/users"      element={<MobileWorkbenchShell surface="admin"><AdminPage group="users" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/operations" element={<MobileWorkbenchShell surface="admin"><AdminPage group="operations" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/marketing"  element={<MobileWorkbenchShell surface="admin"><AdminPage group="marketing" /></MobileWorkbenchShell>} />
      <Route path="/admin/editing"    element={<MobileWorkbenchShell surface="admin"><AdminPage group="editing" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/it"         element={<MobileWorkbenchShell surface="admin"><AdminPage group="it" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/indie-reader" element={<MobileWorkbenchShell surface="admin"><AdminPage group="indie_reader" /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/email-translator" element={<MobileWorkbenchShell surface="admin"><AdminEmailTranslator /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/emails" element={<MobileWorkbenchShell surface="admin"><AdminEmailLogPage /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/inbox" element={<MobileWorkbenchShell surface="admin"><AdminInboxPanel /><AdminExecDock /></MobileWorkbenchShell>} />
      <Route path="/admin/reader-languages" element={<MobileWorkbenchShell surface="admin"><ReaderLanguagesInsightPanel /><AdminExecDock /></MobileWorkbenchShell>} />

      {/* Feb-2026 — Dedicated Editor Workspace (separate from Loom + Admin). */}
      <Route path="/editor/*" element={<EditorApp />} />
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/training" element={<RequireAuth><TrainingPage /></RequireAuth>} />
      <Route path="/manuscript-library" element={<RequireAuth><ManuscriptLibraryPage /></RequireAuth>} />
      {/* The Loom workspace — auth required */}
      <Route
        path="/app/*"
        element={
          <RequireAuth>
            <ProWriterProvider>
              <MobileWorkbenchShell surface="loom">
                <ResignBanner />
                <DunningBanner />
                <GrandfatherBanner />
                <AppShell />
                <GuidedTour />
                <GlowNudge />
              </MobileWorkbenchShell>
            </ProWriterProvider>
          </RequireAuth>
        }
      />
      {/* iter-35 — community forum */}
      <Route path="/community" element={<RequireAuth><CommunityPage /></RequireAuth>} />
      {/* iter55g7 — public Character Codex (no auth required for browse). */}
      <Route path="/codex" element={<CodexBrowsePage />} />
      {/* iter55f62 — Mobile-first Writer's Notebook. Auth-gated, but
       * once the session cookie is cached on the phone the author can
       * jump in without re-signing. */}
      <Route path="/notebook" element={<RequireAuth><MobileNotebookPage /></RequireAuth>} />
      {/* iter55f72 — Novel Framework Builder */}
      <Route path="/framework"       element={<RequireAuth><NovelFrameworkPage /></RequireAuth>} />
      <Route path="/framework/:fid"  element={<RequireAuth><NovelFrameworkPage /></RequireAuth>} />
      {/* iter55f91 — Pop-out panel window (Chapters / Characters / Notebook). */}
      <Route path="/panel-window/:panelId" element={<PanelWindowPage />} />
      {/* iter-50b — Stacks (catalogue search) + iter-50c Funds (writing-services balance) */}
      <Route path="/stacks" element={<RequireAuth><StacksPage /></RequireAuth>} />
      <Route path="/funds"  element={<RequireAuth><FundsPage  /></RequireAuth>} />
      {/* iter-53n++ pass 6 — Public marketplace browse (buy requires auth). */}
      <Route path="/marketplace" element={<MarketplacePage />} />
      {/* iter55g50 — Author Business Dashboard (writing progress, sales,
       * cost per book, marketing marketplace). Auth required. */}
      <Route path="/app/business" element={<RequireAuth><MobileWorkbenchShell surface="loom"><BusinessDashboard /></MobileWorkbenchShell></RequireAuth>} />
      {/* iter55g52 — Admin marketing-bookings console. */}
      <Route path="/admin/marketing-bookings" element={<RequireAuth><MobileWorkbenchShell surface="admin"><AdminMarketingBookings /></MobileWorkbenchShell></RequireAuth>} />
      {/* iter-53n++ pass 8 — Stripe Checkout success return page. */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      {/* iter-50k — One-click unsubscribe from help-update broadcasts */}
      <Route path="/help/unsubscribe" element={<HelpUnsubscribePage />} />
      {/* Fallback — drop to landing rather than 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function CheckingSplash() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="relative">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-panel-active">
            <BookOpen className="h-6 w-6" strokeWidth={2.25} />
          </div>
          <Loader2 className="absolute -bottom-1 -right-1 h-4 w-4 animate-spin text-primary" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">The Loom</div>
      </div>
    </div>
  );
}

// iter-50p — device-aware landing router. Desktop renders the existing
// QuillAndVineLanding; mobile users are REDIRECTED to /bookstore per
// iter-53o pass 5 product decision ("the offered page for the mobile
// devices must be the optimized bookstore"). Using <Navigate replace>
// updates the URL bar to `/bookstore` (cleaner state, no back-button
// dead-end back to `/`).
// Feb-2026 — Bookstore-open switch. Every reader visit to `/` now
// lands directly in the bookstore. The Loom's writer-side desktop
// landing has moved to `/writers` so admins + returning writers can
// still reach it. This is the intentional default for the Feb-2026
// commercial launch — reverse by pointing the `/` route back at
// `<DeviceLandingRouter />`.
function DeviceLandingRouter() {
  const isSmallPhone = useIsSmallPhone();
  const { refresh } = useAuth();
  const [salonOpen, setSalonOpen] = React.useState(false);
  const [salonMode, setSalonMode] = React.useState('signin');
  // 2026-10 · SEO audit follow-up — this is the actual public "/" route
  // (not the admin live-preview, which renders LandingView directly and
  // never mounts this wrapper), so it's safe to apply the backend's
  // homepage title/robots meta here without touching admin state.
  useSeoMeta('/', { title: 'Crown & Rose — Multi-Model AI Writing Studio & Book Store' });
  // 2026-10 — Subscription-checkout return. Every in-app upgrade path
  // (PaywallDialog's Subscribe tab, MyBillingPanel's "Choose a plan")
  // passes `window.location.origin` with no path as `origin_url`, so
  // Stripe's success_url always lands back here on "/" — not "/app" —
  // regardless of where the upgrade was started from.
  useSubscriptionReturn(refresh);
  // Feb-2026 · Mobile phone visitors are sent directly to the Indie
  // Reader child app. When native iOS / Android ship we'll swap the
  // redirect for App Store / Google Play deep-links.
  if (isSmallPhone) return <Navigate to="/bookstore?writer_device=small" replace />;
  // Desktop renders the shared writer landing directly (previously
  // wrapped by QuillAndVineLanding — removed Feb-2026).
  return (
    <>
      <LandingView
        onEmailSignin={() => { setSalonMode('signin'); setSalonOpen(true); }}
        onStartTrial={() => { setSalonMode('signup'); setSalonOpen(true); }}
      />
      <WritersSalonModal
        open={salonOpen}
        initialMode={salonMode}
        onClose={() => setSalonOpen(false)}
      />
    </>
  );
}

function DeviceBookstoreRouter() {
  // iter55z-audit — Companion reader app removed. Mobile and desktop
  // both render the same BookstoreLanding; the mobile-specific view was
  // part of the deleted reader surfaces.
  return <><SmallDeviceBookstoreNotice /><BookstoreLanding /></>;
}

function RequireAuth({ children }) {
  const { user, checking } = useAuth();
  const location = useLocation();
  if (checking) return <CheckingSplash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />;
  /* iter-72h/i — soft-expiry gate. Once a trial user's `trial_expired`
   * flag is true, replace the shell with the TrialExpiredScreen so
   * they land on a friendly upgrade page instead of watching the app
   * 401 on every action. Exception: when the URL carries
   * `?view=billing` we let them through — otherwise the "Pick a plan"
   * CTA can't actually open the billing dialog (chicken/egg). */
  const wantsBilling = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('view') === 'billing';
  if (user.trial_expired && !wantsBilling) return <TrialExpiredScreen />;
  return children;
}

function PublicOnly({ children }) {
  const { user, checking } = useAuth();
  const location = useLocation();
  if (checking) return <CheckingSplash />;
  // iter-23 — logged-in users hitting /login go straight into the app.
  // iter-72b — …unless they came here via the Margin rail deep-link
  // (`/login#community`), in which case we let them through so they
  // can see the forum stream + inline composer.
  // Feb-2026 — Honour the `from` state RequireAuth pushed when it
  // bounced the visitor here (e.g. bookmark → Writer's Notebook while
  // signed out). Fall back to /app so cold /login hits still land in
  // the Loom.
  const viaCommunity = typeof window !== 'undefined'
    && window.location.hash === '#community';
  const from = (location.state && location.state.from) || '';
  if (user && !viaCommunity) {
    // Only redirect back to `from` if it's an internal, non-auth path
    // — never bounce someone into /login or an external URL.
    const target = (typeof from === 'string' && from.startsWith('/')
      && !from.startsWith('/login')) ? from : '/app';
    return <Navigate to={target} replace />;
  }
  return children;
}

const FIXED_WORKSPACE_ROOTS = [
  '/app', '/admin', '/loom', '/editor', '/qa', '/quality',
  '/panel-window', '/notebook', '/framework',
];

function RouteAwareAppFrame({ children }) {
  const { pathname } = useLocation();
  const frameRef = React.useRef(null);
  const fixedWorkspace = FIXED_WORKSPACE_ROOTS.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`),
  );

  React.useEffect(() => {
    frameRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <div
      ref={frameRef}
      className={`App h-screen w-screen bg-background text-foreground antialiased ${fixedWorkspace ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto'}`}
      data-testid="app-scroll-frame"
      data-scroll-mode={fixedWorkspace ? 'workspace' : 'page'}
    >
      {children}
    </div>
  );
}

function App() {
  return (
    <TooltipProvider delayDuration={200}>
      <BrowserRouter>
        <AuthProvider>
          <BookstoreAuthProvider>
            <CurrencyProvider>
              <DemoProvider>
                <RouteAwareAppFrame>
                  <ClarityTracker />
                  <AppRouter />
                  {/* Feb-2026 — Pre-launch "test mode only" banner.
                   * Painted over EVERY route; pointer-events-none so it
                   * never blocks clicks / selection on the content
                   * below. Admin toggles it off from Admin → Landing. */}
                  <PaymentDowntimeBanner />
                  <PreLaunchBanner />
                  <Toaster richColors position="bottom-right" />
                  <PaywallProvider />
                  {/* iter55g50 — Warns writers 5 min before their session
                   * cookie expires, with a one-tap Extend button. Renders
                   * nothing until the countdown enters the warning window. */}
                  <SessionTimeoutWarning />
                  {/* iter-72o — DemoUpgradeHost + TrialUpgradeDialog consolidated
                   * into a single upgrade-flow host so App.js only wires it
                   * once. See `/app/frontend/src/components/UpgradeManager.jsx`. */}
                  <UpgradeManager />
                  <AliasClaimModal />
                  <AiHelpLauncher />
                  {/* Feb-2026 · click-through EULA gate. Renders
                   *   nothing when the user is already up-to-date. */}
                  <EulaGate />
                </RouteAwareAppFrame>
              </DemoProvider>
            </CurrencyProvider>
          </BookstoreAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  );
}

// iter-72o — DemoUpgradeHost has been consolidated into UpgradeManager.

export default App;
